import { ScrollArea } from '@mantine/core';
import { EnrollmentTag } from '@zorro/clients';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Stack } from '@zorro/web/design-system';
import { useBatchCallEndpoint } from '@zorro/web/ui-utils';
import { useState } from 'react';
import { $enum } from 'ts-enum-util';

import { FormFooter } from '../FormFooter';
import {
  EditEnrollmentTagsRecord,
  TagSelectionState,
  getNextTagState,
  getRecordEnrollmentTags,
  mapEnrollmentTagRecordToTagStates,
} from './EditEnrollmentTagsUtils';
import { EnrollmentTagBadge } from './EnrollmentTagBadge';
import { ENROLLMENT_TAG_ORDER } from './enrollmentTagConfig';

const TAG_LIST_MAX_HEIGHT = 240;

type Props = {
  records: EditEnrollmentTagsRecord[];
  onClose: () => void;
  onSubmit?: () => void;
};

export const EditEnrollmentTags = ({ records, onClose, onSubmit }: Props) => {
  const initialTagStates = mapEnrollmentTagRecordToTagStates(records);

  const [tagStates, setTagStates] = useState(initialTagStates);

  const isDirty = $enum(EnrollmentTag)
    .getValues()
    .some((tag) => tagStates[tag] !== initialTagStates[tag]);

  const { executeBatchCall } = useBatchCallEndpoint({
    methodName: 'onboardingPeriodsControllerUpdateEnrollmentTags',
    singularItemName: 'enrollment record',
    action: 'updated',
  });

  const handleTagChange = (tag: EnrollmentTag) => {
    setTagStates((previousStates) => ({
      ...previousStates,
      [tag]: getNextTagState(previousStates[tag], initialTagStates[tag]),
    }));
  };

  async function handleSubmit() {
    onSubmit?.();

    const batchItems = records.map((record) => ({
      key: record.fullName,
      params: [
        record.onboardingPeriodId,
        { enrollmentTags: getRecordEnrollmentTags(record, tagStates) },
      ] as const,
    }));

    await executeBatchCall(batchItems, {
      onSuccess: onClose,
      shouldAvoidPageReload: true,
    });
  }

  return (
    <Stack p="var(--space-12)" gap="var(--space-12)" miw="17rem">
      <ScrollArea.Autosize
        type="always"
        mah={TAG_LIST_MAX_HEIGHT}
        offsetScrollbars="y"
      >
        <Stack gap="var(--space-12)">
          {ENROLLMENT_TAG_ORDER.map((tag) => (
            <Checkbox
              key={tag}
              checked={tagStates[tag] === TagSelectionState.ALL}
              indeterminate={tagStates[tag] === TagSelectionState.SOME}
              onChange={() => handleTagChange(tag)}
              label={<EnrollmentTagBadge tag={tag} />}
            />
          ))}
        </Stack>
      </ScrollArea.Autosize>
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{
          size: 'sm',
          disabled: !isDirty,
          onClick: handleSubmit,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{ size: 'sm', onClick: onClose }}
      />
    </Stack>
  );
};
